<template>
  <TransitionRoot appear :show="open" as="template">
    <Dialog as="div" class="relative z-50" @close="handleClose">
      <!-- Backdrop -->
      <TransitionChild
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-black bg-opacity-25" />
      </TransitionChild>

      <div class="fixed inset-0 overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-4 text-center">
          <!-- Modal panel -->
          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <DialogPanel :class="modalClasses">
              <!-- Header -->
              <div
                v-if="slots.header || title"
                class="flex items-center justify-between p-6 border-b border-gray-200"
              >
                <div class="flex items-center">
                  <slot name="header">
                    <DialogTitle as="h3" class="text-lg font-medium leading-6 text-gray-900">
                      {{ title }}
                    </DialogTitle>
                  </slot>
                </div>
                <button
                  v-if="closable"
                  @click="handleClose"
                  class="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <span class="sr-only">Close</span>
                  <X class="h-6 w-6" />
                </button>
              </div>

              <!-- Body -->
              <div :class="bodyClasses">
                <slot />
              </div>

              <!-- Footer -->
              <div
                v-if="slots.footer"
                class="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50"
              >
                <slot name="footer" />
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup lang="ts">
import { computed, useSlots } from 'vue'
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue'
import { X } from 'lucide-vue-next'

export interface BaseModalProps {
  open: boolean
  title?: string
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full'
  closable?: boolean
  persistent?: boolean
}

const props = withDefaults(defineProps<BaseModalProps>(), {
  size: 'md',
  closable: true,
  persistent: false,
})

const emit = defineEmits<{
  close: []
  'update:open': [value: boolean]
}>()

const slots = useSlots()

const modalClasses = computed(() => {
  const baseClasses = [
    'w-full transform overflow-hidden rounded-lg bg-white text-left align-middle shadow-xl transition-all',
  ]

  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    '2xl': 'max-w-6xl',
    full: 'max-w-full mx-4',
  }

  return [...baseClasses, sizeClasses[props.size]].join(' ')
})

const bodyClasses = computed(() => {
  const baseClasses = ['p-6']

  // Remove top padding if there's a header
  if (props.title || slots.header) {
    baseClasses.push('pt-0')
  }

  // Remove bottom padding if there's a footer
  if (slots.footer) {
    baseClasses.push('pb-0')
  }

  return baseClasses.join(' ')
})

const handleClose = () => {
  if (!props.persistent) {
    emit('close')
    emit('update:open', false)
  }
}
</script>
